import { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, formatMoney } from '@/lib/api';
import { AdCard } from '@/components/marketplace/AdCard';
import { SafeImage } from '@/components/marketplace/SafeImage';
import {
    Star, MapPin, Clock, Phone, MessageSquare, Truck, CreditCard, Banknote,
    Share2, QrCode, Heart, ChevronRight, Facebook, Instagram, Youtube,
    CheckCircle2, AlertCircle, ExternalLink, ShoppingBag, ArrowLeft, Store,
    Zap, Package, Globe, Info, CreditCard as CardIcon, Navigation
} from 'lucide-react';

// ─── constants ────────────────────────────────────────────────────────────────

const SUPER_CAT_META = {
    oklovendo:     { name: 'OkLoVendo',    icon: '🛍️', color: '#dc2626', gradient: 'from-red-600 to-orange-500' },
    okfood:        { name: 'OkFood',       icon: '🍔', color: '#ea580c', gradient: 'from-orange-600 to-amber-500' },
    okrestaurante: { name: 'OkRestaurante',icon: '🍽️', color: '#1d4ed8', gradient: 'from-blue-700 to-blue-500' },
    oktienda:      { name: 'OkTienda',     icon: '🏪', color: '#16a34a', gradient: 'from-green-700 to-emerald-500' },
    okshop:        { name: 'OkShop',       icon: '🏬', color: '#7c3aed', gradient: 'from-violet-700 to-purple-500' },
    okfarmacia:    { name: 'OkFarma',      icon: '💊', color: '#0891b2', gradient: 'from-cyan-700 to-teal-500' },
    oktortilla:    { name: 'OkTortilla',   icon: '🌽', color: '#d97706', gradient: 'from-amber-600 to-yellow-500' },
    okdrink:       { name: 'OkDrink',      icon: '🍺', color: '#b45309', gradient: 'from-yellow-700 to-amber-600' },
};

// ─── star rating ──────────────────────────────────────────────────────────────

function StarRating({ value, count, size = 'sm' }) {
    const full  = Math.floor(value);
    const half  = value - full >= 0.5;
    const empty = 5 - full - (half ? 1 : 0);
    const sz    = size === 'lg' ? 'h-5 w-5' : 'h-3.5 w-3.5';
    return (
        <div className="flex items-center gap-1.5">
            <div className="flex gap-0.5">
                {[...Array(full)].map((_, i)  => <Star key={`f${i}`}  className={`${sz} fill-amber-400 text-amber-400`} />)}
                {half &&                          <Star key="h"         className={`${sz} fill-amber-200 text-amber-400`} />}
                {[...Array(empty)].map((_, i) => <Star key={`e${i}`}  className={`${sz} text-gray-200`} />)}
            </div>
            <span className={`font-black ${size === 'lg' ? 'text-2xl' : 'text-sm'} text-gray-900`}>{value.toFixed(1)}</span>
            {count > 0 && (
                <span className={`${size === 'lg' ? 'text-sm' : 'text-xs'} text-gray-400`}>
                    ({count.toLocaleString()} reseñas)
                </span>
            )}
        </div>
    );
}

// ─── stat chip ────────────────────────────────────────────────────────────────

function StatChip({ icon: Icon, label, value, accent = false }) {
    return (
        <div className={`flex items-center gap-2 px-4 py-2.5 rounded-2xl border ${accent ? 'bg-green-50 border-green-200' : 'bg-white border-gray-100'}`}>
            <Icon className={`h-4 w-4 flex-shrink-0 ${accent ? 'text-green-600' : 'text-gray-400'}`} />
            <div>
                <p className={`text-xs font-bold ${accent ? 'text-green-700' : 'text-gray-900'}`}>{value}</p>
                <p className="text-[10px] text-gray-400 leading-none mt-0.5">{label}</p>
            </div>
        </div>
    );
}

// ─── payment badge ────────────────────────────────────────────────────────────

function PaymentBadge({ method }) {
    const map = {
        card:     { label: 'Tarjeta',      icon: '💳', bg: 'bg-blue-50   text-blue-700  border-blue-200' },
        cash:     { label: 'Efectivo',     icon: '💵', bg: 'bg-green-50  text-green-700 border-green-200' },
        transfer: { label: 'Transferencia',icon: '🏦', bg: 'bg-violet-50 text-violet-700 border-violet-200' },
    };
    const m = map[method] || { label: method, icon: '💳', bg: 'bg-gray-50 text-gray-700 border-gray-200' };
    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold ${m.bg}`}>
            {m.icon} {m.label}
        </span>
    );
}

// ─── schedule row ─────────────────────────────────────────────────────────────

function ScheduleRows({ schedule }) {
    const entries = Object.entries(schedule || {});
    if (!entries.length) return null;
    return (
        <div className="space-y-2">
            {entries.map(([key, val]) => (
                <div key={key} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <span className="text-sm text-gray-500 capitalize">{key.replace(/_/g, ' ')}</span>
                    <span className="text-sm font-semibold text-gray-900">{val}</span>
                </div>
            ))}
        </div>
    );
}

// ─── tab button ───────────────────────────────────────────────────────────────

function Tab({ id, label, icon: Icon, active, onClick, badge }) {
    return (
        <button
            onClick={() => onClick(id)}
            className={`flex items-center gap-1.5 px-5 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors relative ${
                active
                    ? 'border-red-600 text-red-600'
                    : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-200'
            }`}
        >
            <Icon className="h-4 w-4" />
            {label}
            {badge != null && badge > 0 && (
                <span className="ml-0.5 text-[10px] font-black bg-red-600 text-white rounded-full px-1.5 py-0.5 leading-none">
                    {badge}
                </span>
            )}
        </button>
    );
}

// ─── skeleton loader ──────────────────────────────────────────────────────────

function ProfileSkeleton() {
    return (
        <div className="min-h-screen bg-gray-50 animate-pulse">
            <div className="h-72 bg-gray-200" />
            <div className="max-w-5xl mx-auto px-4">
                <div className="-mt-16 flex gap-4 items-end mb-6">
                    <div className="h-28 w-28 rounded-3xl bg-gray-300 flex-shrink-0" />
                    <div className="flex-1 space-y-2 mb-2">
                        <div className="h-7 w-52 bg-gray-200 rounded-xl" />
                        <div className="h-4 w-36 bg-gray-100 rounded-lg" />
                        <div className="h-4 w-24 bg-gray-100 rounded-lg" />
                    </div>
                </div>
                <div className="flex gap-3 mb-6">
                    {[...Array(3)].map((_, i) => <div key={i} className="h-14 w-36 bg-gray-100 rounded-2xl" />)}
                </div>
                <div className="h-12 bg-gray-100 rounded-2xl mb-4" />
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {[...Array(6)].map((_, i) => <div key={i} className="aspect-[4/5] bg-gray-100 rounded-2xl" />)}
                </div>
            </div>
        </div>
    );
}

// ─── error states ─────────────────────────────────────────────────────────────

function NotFound({ slug }) {
    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center text-center px-4">
            <div className="h-24 w-24 rounded-3xl bg-gray-100 flex items-center justify-center text-5xl mb-6 shadow-inner">
                🏪
            </div>
            <h1 className="text-2xl font-black text-gray-900 mb-2">Negocio no encontrado</h1>
            <p className="text-gray-500 mb-6 max-w-sm">
                El perfil <code className="bg-gray-100 px-2 py-0.5 rounded text-sm">/aliado/{slug}</code> no existe o ya no está disponible.
            </p>
            <Link to="/okgo">
                <button className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 hover:bg-red-700 text-white font-bold text-sm rounded-full transition-colors shadow-lg">
                    Explorar OkGo <ChevronRight className="h-4 w-4" />
                </button>
            </Link>
        </div>
    );
}

function ErrorState() {
    return (
        <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center text-center px-4">
            <AlertCircle className="h-14 w-14 text-red-400 mb-4" />
            <p className="font-semibold text-gray-700 mb-1">Error al cargar el negocio</p>
            <p className="text-sm text-gray-400 mb-5">Verifica tu conexión e intenta de nuevo</p>
            <button
                onClick={() => window.location.reload()}
                className="px-5 py-2.5 border border-gray-200 hover:border-gray-300 text-gray-700 font-semibold text-sm rounded-full transition-colors"
            >
                Reintentar
            </button>
        </div>
    );
}

// ─── main component ───────────────────────────────────────────────────────────

export default function OkNegocioProfile() {
    const { slug }    = useParams();
    const navigate    = useNavigate();
    const tabsRef     = useRef(null);
    const [biz, setBiz]           = useState(null);
    const [ads, setAds]           = useState([]);
    const [loading, setLoading]   = useState(true);
    const [error, setError]       = useState(null);
    const [activeTab, setActiveTab] = useState('catalogo');
    const [tabsSticky, setTabsSticky] = useState(false);
    const [shared, setShared]     = useState(false);

    // Load business + its ads
    useEffect(() => {
        setLoading(true);
        setError(null);
        api.get(`/oknegocios/${slug}`)
            .then(async ({ data }) => {
                setBiz(data);
                if (data.owner_nickname) {
                    try {
                        const res = await api.get('/ads', { params: { seller: data.owner_nickname, limit: 24 } });
                        const raw = res.data;
                        setAds(Array.isArray(raw?.items) ? raw.items : Array.isArray(raw?.ads) ? raw.ads : Array.isArray(raw) ? raw : []);
                    } catch { setAds([]); }
                } else { setAds([]); }
            })
            .catch(err => {
                setError(err?.response?.status === 404 ? 'not-found' : 'error');
            })
            .finally(() => setLoading(false));
    }, [slug]);

    // Sticky tab detection
    useEffect(() => {
        const el = tabsRef.current;
        if (!el) return;
        const obs = new IntersectionObserver(
            ([e]) => setTabsSticky(e.intersectionRatio < 1),
            { threshold: 1, rootMargin: '-64px 0px 0px 0px' }
        );
        obs.observe(el);
        return () => obs.disconnect();
    }, [biz]);

    const handleShare = () => {
        const url  = window.location.href;
        const data = { title: biz?.name, text: biz?.tagline, url };
        if (navigator.share && navigator.canShare?.(data)) navigator.share(data);
        else { navigator.clipboard.writeText(url); setShared(true); setTimeout(() => setShared(false), 2000); }
    };

    if (loading) return <ProfileSkeleton />;
    if (error === 'not-found') return <NotFound slug={slug} />;
    if (error) return <ErrorState />;

    const cat         = SUPER_CAT_META[biz.super_category] || { name: biz.super_category, icon: '🏪', color: '#dc2626', gradient: 'from-red-600 to-orange-500' };
    const hasSchedule = biz.schedule && Object.keys(biz.schedule).length > 0;
    const isFood      = ['okfood', 'okrestaurante'].includes(biz.super_category);
    const tabLabel    = isFood ? 'Menú' : 'Catálogo';
    const waUrl       = biz.whatsapp
        ? `https://wa.me/52${biz.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(`Hola ${biz.name}, vi tu perfil en OkGo 👋`)}`
        : null;

    return (
        <div className="min-h-screen bg-gray-50 pb-24 sm:pb-0">

            {/* ── Floating top bar ─────────────────────────────────────────── */}
            <div className="fixed top-0 inset-x-0 z-40 flex items-center justify-between px-4 py-3 pointer-events-none">
                <button
                    onClick={() => navigate(-1)}
                    className="pointer-events-auto h-10 w-10 rounded-full bg-white/90 backdrop-blur-sm shadow-lg border border-white/60 flex items-center justify-center hover:bg-white transition-colors"
                >
                    <ArrowLeft className="h-4 w-4 text-gray-700" />
                </button>
                <div className="pointer-events-auto flex items-center gap-2">
                    <button
                        onClick={handleShare}
                        className="h-10 w-10 rounded-full bg-white/90 backdrop-blur-sm shadow-lg border border-white/60 flex items-center justify-center hover:bg-white transition-colors"
                    >
                        {shared
                            ? <CheckCircle2 className="h-4 w-4 text-green-600" />
                            : <Share2 className="h-4 w-4 text-gray-700" />
                        }
                    </button>
                </div>
            </div>

            {/* ── Hero cover ───────────────────────────────────────────────── */}
            <div className="relative h-64 sm:h-80 overflow-hidden">
                {biz.cover_url ? (
                    <SafeImage src={biz.cover_url} alt={biz.name} className="w-full h-full object-cover" />
                ) : (
                    <div className={`w-full h-full bg-gradient-to-br ${cat.gradient} flex items-center justify-center`}>
                        <span className="text-[120px] opacity-20 select-none">{cat.icon}</span>
                    </div>
                )}
                {/* multi-stop overlay */}
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/70" />

                {/* Category pill — bottom left of cover */}
                <div className="absolute bottom-4 left-4">
                    <span
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-xs font-bold shadow-lg backdrop-blur-sm border border-white/20"
                        style={{ backgroundColor: `${cat.color}cc` }}
                    >
                        {cat.icon} {cat.name}
                    </span>
                </div>

                {/* Verified pill — bottom right */}
                {biz.verified && (
                    <div className="absolute bottom-4 right-4">
                        <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-blue-600/90 backdrop-blur-sm text-white text-xs font-bold shadow-lg border border-white/20">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Verificado
                        </span>
                    </div>
                )}
            </div>

            {/* ── Identity strip ───────────────────────────────────────────── */}
            <div className="bg-white shadow-sm">
                <div className="max-w-5xl mx-auto px-4">
                    <div className="flex gap-4 items-end -mt-14 pb-4 relative z-10">
                        {/* Logo */}
                        <div
                            className="h-24 w-24 sm:h-28 sm:w-28 rounded-3xl bg-white shadow-xl border-4 border-white flex items-center justify-center overflow-hidden flex-shrink-0"
                            style={{ boxShadow: `0 8px 32px ${cat.color}33, 0 2px 8px rgba(0,0,0,0.12)` }}
                        >
                            {biz.logo_url
                                ? <SafeImage src={biz.logo_url} alt={biz.name} className="w-full h-full object-cover" />
                                : <span className="text-5xl select-none">{cat.icon}</span>
                            }
                        </div>

                        {/* Name + tagline + rating */}
                        <div className="flex-1 min-w-0 pt-16">
                            <div className="flex items-start gap-2 flex-wrap">
                                <h1 className="text-2xl sm:text-3xl font-black text-gray-900 leading-tight">{biz.name}</h1>
                                {biz.verified && <CheckCircle2 className="h-5 w-5 text-blue-500 flex-shrink-0 mt-1" />}
                            </div>
                            {biz.tagline && (
                                <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{biz.tagline}</p>
                            )}
                            {biz.rating_avg > 0 && (
                                <div className="mt-2">
                                    <StarRating value={biz.rating_avg} count={biz.rating_count} />
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Stats bar ────────────────────────────────────────────────── */}
            <div className="bg-white border-t border-gray-50">
                <div className="max-w-5xl mx-auto px-4 py-3">
                    <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-0.5">
                        {biz.delivery_available && (
                            <StatChip
                                icon={Truck}
                                label="Envío"
                                value={biz.delivery_cost === 0 ? 'Gratis' : formatMoney(biz.delivery_cost)}
                                accent={biz.delivery_cost === 0}
                            />
                        )}
                        {biz.min_order > 0 && (
                            <StatChip icon={ShoppingBag} label="Pedido mín." value={formatMoney(biz.min_order)} />
                        )}
                        {hasSchedule && (
                            <StatChip icon={Clock} label="Hoy" value={Object.values(biz.schedule)[0] || 'Ver horario'} />
                        )}
                        {biz.city && (
                            <StatChip icon={MapPin} label="Ciudad" value={biz.city} />
                        )}
                        {biz.phone && (
                            <a href={`tel:${biz.phone}`}>
                                <StatChip icon={Phone} label="Llamar" value={biz.phone} />
                            </a>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Action buttons ───────────────────────────────────────────── */}
            <div className="bg-white border-t border-gray-50 shadow-sm">
                <div className="max-w-5xl mx-auto px-4 py-3 flex gap-2 overflow-x-auto scrollbar-hide">
                    {waUrl && (
                        <a href={waUrl} target="_blank" rel="noreferrer"
                            className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-green-500 hover:bg-green-600 text-white font-bold text-sm rounded-full transition-colors shadow-sm"
                        >
                            <MessageSquare className="h-4 w-4" /> WhatsApp
                        </a>
                    )}
                    {biz.city && (
                        <a
                            href={`https://maps.google.com/?q=${encodeURIComponent(`${biz.address || ''} ${biz.city}`)}`}
                            target="_blank" rel="noreferrer"
                            className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-white hover:bg-gray-50 text-gray-700 font-bold text-sm rounded-full border border-gray-200 transition-colors"
                        >
                            <Navigation className="h-4 w-4 text-blue-500" /> Cómo llegar
                        </a>
                    )}
                    {biz.phone && (
                        <a href={`tel:${biz.phone}`}
                            className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-white hover:bg-gray-50 text-gray-700 font-bold text-sm rounded-full border border-gray-200 transition-colors"
                        >
                            <Phone className="h-4 w-4 text-gray-500" /> Llamar
                        </a>
                    )}
                    {biz.social_links?.instagram && (
                        <a href={biz.social_links.instagram} target="_blank" rel="noreferrer"
                            className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-pink-500 to-purple-600 text-white font-bold text-sm rounded-full transition-opacity hover:opacity-90"
                        >
                            <Instagram className="h-4 w-4" /> Instagram
                        </a>
                    )}
                    {biz.social_links?.facebook && (
                        <a href={biz.social_links.facebook} target="_blank" rel="noreferrer"
                            className="flex-shrink-0 inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm rounded-full transition-colors"
                        >
                            <Facebook className="h-4 w-4" /> Facebook
                        </a>
                    )}
                </div>
            </div>

            {/* ── Sticky tabs ──────────────────────────────────────────────── */}
            <div
                ref={tabsRef}
                className={`sticky top-0 z-30 bg-white transition-shadow ${tabsSticky ? 'shadow-md' : 'border-b border-gray-100'}`}
            >
                <div className="max-w-5xl mx-auto px-4">
                    <div className="flex overflow-x-auto scrollbar-hide">
                        <Tab id="catalogo" label={tabLabel}    icon={ShoppingBag} active={activeTab === 'catalogo'} onClick={setActiveTab} badge={ads.length} />
                        <Tab id="info"     label="Información" icon={Info}        active={activeTab === 'info'}     onClick={setActiveTab} />
                        <Tab id="pagos"    label="Pagos"       icon={CreditCard}  active={activeTab === 'pagos'}    onClick={setActiveTab} />
                        {hasSchedule && (
                            <Tab id="horario" label="Horario" icon={Clock} active={activeTab === 'horario'} onClick={setActiveTab} />
                        )}
                    </div>
                </div>
            </div>

            {/* ── Tab content ──────────────────────────────────────────────── */}
            <div className="max-w-5xl mx-auto px-4 py-6">

                {/* CATÁLOGO / MENÚ */}
                {activeTab === 'catalogo' && (
                    <div>
                        {(!Array.isArray(ads) || ads.length === 0) ? (
                            <div className="flex flex-col items-center justify-center py-20 text-center bg-white rounded-3xl border border-dashed border-gray-200">
                                <div className="h-20 w-20 rounded-3xl bg-gray-50 flex items-center justify-center text-4xl mb-4 shadow-inner">
                                    {cat.icon}
                                </div>
                                <p className="font-bold text-gray-700 mb-1">
                                    {isFood ? 'Sin menú publicado aún' : 'Sin productos publicados aún'}
                                </p>
                                <p className="text-sm text-gray-400 max-w-xs">
                                    {biz.is_demo
                                        ? 'Este es un negocio demo. Los productos aparecerán aquí cuando el negocio esté en producción.'
                                        : 'Este negocio aún no ha subido productos. ¡Vuelve pronto!'}
                                </p>
                                {waUrl && (
                                    <a href={waUrl} target="_blank" rel="noreferrer" className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-green-500 hover:bg-green-600 text-white font-bold text-sm rounded-full transition-colors">
                                        <MessageSquare className="h-4 w-4" /> Preguntar por WhatsApp
                                    </a>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="flex items-center justify-between mb-4">
                                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                                        {ads.length} {isFood ? (ads.length === 1 ? 'platillo' : 'platillos') : (ads.length === 1 ? 'producto' : 'productos')}
                                    </p>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                                    {(Array.isArray(ads) ? ads : []).map(ad => (
                                        <AdCard key={ad.id} ad={ad} />
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* INFORMACIÓN */}
                {activeTab === 'info' && (
                    <div className="space-y-4">
                        {/* About */}
                        {biz.description && (
                            <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-50">
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Sobre el negocio</p>
                                <p className="text-sm text-gray-700 leading-relaxed">{biz.description}</p>
                            </div>
                        )}

                        {/* Location */}
                        {(biz.address || biz.city) && (
                            <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-50">
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Ubicación</p>
                                <div className="flex items-start gap-3">
                                    <div className="h-10 w-10 rounded-2xl bg-red-50 flex items-center justify-center flex-shrink-0">
                                        <MapPin className="h-5 w-5 text-red-500" />
                                    </div>
                                    <div>
                                        {biz.address && <p className="text-sm font-semibold text-gray-800">{biz.address}</p>}
                                        {biz.city && <p className="text-sm text-gray-500">{biz.city}</p>}
                                        {biz.city && (
                                            <a
                                                href={`https://maps.google.com/?q=${encodeURIComponent(`${biz.address || ''} ${biz.city}`)}`}
                                                target="_blank" rel="noreferrer"
                                                className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-700"
                                            >
                                                Ver en Google Maps <ExternalLink className="h-3 w-3" />
                                            </a>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Delivery */}
                        <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-50">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Entrega</p>
                            <div className="flex items-start gap-3">
                                <div className={`h-10 w-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${biz.delivery_available ? 'bg-green-50' : 'bg-gray-100'}`}>
                                    <Truck className={`h-5 w-5 ${biz.delivery_available ? 'text-green-600' : 'text-gray-400'}`} />
                                </div>
                                <div>
                                    <p className={`text-sm font-bold ${biz.delivery_available ? 'text-green-700' : 'text-gray-500'}`}>
                                        {biz.delivery_available ? 'Delivery disponible' : 'Sin delivery por ahora'}
                                    </p>
                                    {biz.delivery_available && (
                                        <div className="mt-1 space-y-0.5">
                                            <p className="text-xs text-gray-500">
                                                Costo de envío: <span className="font-semibold text-gray-800">
                                                    {biz.delivery_cost === 0 ? 'Gratis 🎉' : formatMoney(biz.delivery_cost)}
                                                </span>
                                            </p>
                                            {biz.min_order > 0 && (
                                                <p className="text-xs text-gray-500">
                                                    Pedido mínimo: <span className="font-semibold text-gray-800">{formatMoney(biz.min_order)}</span>
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Contact */}
                        {(biz.phone || biz.email || biz.whatsapp) && (
                            <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-50">
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">Contacto</p>
                                <div className="space-y-3">
                                    {biz.phone && (
                                        <a href={`tel:${biz.phone}`} className="flex items-center gap-3 group">
                                            <div className="h-9 w-9 rounded-xl bg-gray-50 flex items-center justify-center">
                                                <Phone className="h-4 w-4 text-gray-500" />
                                            </div>
                                            <span className="text-sm font-medium text-gray-700 group-hover:text-red-600 transition-colors">{biz.phone}</span>
                                        </a>
                                    )}
                                    {biz.whatsapp && (
                                        <a href={waUrl} target="_blank" rel="noreferrer" className="flex items-center gap-3 group">
                                            <div className="h-9 w-9 rounded-xl bg-green-50 flex items-center justify-center">
                                                <MessageSquare className="h-4 w-4 text-green-600" />
                                            </div>
                                            <span className="text-sm font-medium text-gray-700 group-hover:text-green-600 transition-colors">WhatsApp</span>
                                            <ExternalLink className="h-3.5 w-3.5 text-gray-300 group-hover:text-green-500" />
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Status badges */}
                        <div className="flex gap-2 flex-wrap">
                            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${biz.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                                <span className={`h-1.5 w-1.5 rounded-full ${biz.status === 'active' ? 'bg-green-500' : 'bg-gray-400'}`} />
                                {biz.status === 'active' ? 'Activo' : biz.status}
                            </span>
                            {biz.verified && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-blue-100 text-blue-700">
                                    <CheckCircle2 className="h-3 w-3" /> Negocio verificado
                                </span>
                            )}
                            {biz.is_demo && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">
                                    <Zap className="h-3 w-3" /> Cuenta demo
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* PAGOS */}
                {activeTab === 'pagos' && (
                    <div className="space-y-4">
                        <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-50">
                            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Métodos de pago aceptados</p>
                            <div className="flex flex-wrap gap-3">
                                {(biz.payment_methods?.length ? biz.payment_methods : ['cash']).map(m => (
                                    <PaymentBadge key={m} method={m} />
                                ))}
                            </div>
                        </div>
                        <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-3xl p-5 text-white">
                            <div className="flex items-center gap-3 mb-3">
                                <div className="h-10 w-10 rounded-2xl bg-white/10 flex items-center justify-center">
                                    <Zap className="h-5 w-5 text-amber-400" />
                                </div>
                                <div>
                                    <p className="font-black text-base">OkWallet</p>
                                    <p className="text-xs text-gray-400">Paga más fácil y rápido</p>
                                </div>
                            </div>
                            <p className="text-sm text-gray-300 mb-4">Recarga tu saldo OkWallet y paga sin efectivo en todos los negocios de la plataforma.</p>
                            <Link to="/dashboard/wallet">
                                <button className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-gray-900 font-bold text-sm rounded-full hover:bg-gray-100 transition-colors">
                                    Recargar OkWallet <ChevronRight className="h-4 w-4" />
                                </button>
                            </Link>
                        </div>
                    </div>
                )}

                {/* HORARIO */}
                {activeTab === 'horario' && hasSchedule && (
                    <div className="bg-white rounded-3xl p-5 shadow-sm border border-gray-50">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="h-10 w-10 rounded-2xl bg-amber-50 flex items-center justify-center">
                                <Clock className="h-5 w-5 text-amber-500" />
                            </div>
                            <div>
                                <p className="font-black text-gray-900">Horarios de atención</p>
                                <p className="text-xs text-gray-400">Horario local (hora Ciudad de México)</p>
                            </div>
                        </div>
                        <ScheduleRows schedule={biz.schedule} />
                    </div>
                )}
            </div>

            {/* ── Footer breadcrumb ─────────────────────────────────────────── */}
            <div className="max-w-5xl mx-auto px-4 pb-6 hidden sm:block">
                <div className="flex items-center gap-1.5 text-xs text-gray-400">
                    <Link to="/okgo" className="hover:text-red-600 transition-colors">OkGo</Link>
                    <ChevronRight className="h-3 w-3" />
                    <span className="hover:text-red-600 cursor-pointer" onClick={() => navigate(`/okgo?cat=${biz.super_category}`)}>
                        {cat.name}
                    </span>
                    <ChevronRight className="h-3 w-3" />
                    <span className="text-gray-600 font-medium">{biz.name}</span>
                </div>
            </div>

            {/* ── Mobile sticky WhatsApp CTA ────────────────────────────────── */}
            {waUrl && (
                <div className="fixed bottom-0 inset-x-0 z-40 sm:hidden bg-white/95 backdrop-blur-md border-t border-gray-100 px-4 py-3 flex gap-2">
                    <a href={waUrl} target="_blank" rel="noreferrer" className="flex-1 flex items-center justify-center gap-2 py-3 bg-green-500 hover:bg-green-600 text-white font-black text-sm rounded-2xl transition-colors shadow-lg">
                        <MessageSquare className="h-4 w-4" /> Contactar por WhatsApp
                    </a>
                    <button
                        onClick={handleShare}
                        className="h-12 w-12 rounded-2xl bg-gray-100 hover:bg-gray-200 flex items-center justify-center flex-shrink-0 transition-colors"
                    >
                        {shared ? <CheckCircle2 className="h-5 w-5 text-green-600" /> : <Share2 className="h-5 w-5 text-gray-600" />}
                    </button>
                </div>
            )}
        </div>
    );
}
